import { useState, type FormEvent } from 'react';
import type { Activity, ActivityListQuery, ManualActivityType } from '@neuroedge/shared';
import { Button, Card, PageState, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import { useActivities, useCreateActivity, useDeleteActivity } from '../../api/activities';

interface Props {
  title?: string;
  query: ActivityListQuery;
  attachTo?: { contactId?: string; leadId?: string };
}

const TYPE_LABEL: Record<Activity['type'], string> = {
  NOTE: 'Note',
  CALL: 'Call',
  EMAIL: 'Email',
  MEETING: 'Meeting',
  WHATSAPP_MESSAGE: 'WhatsApp',
  STAGE_CHANGE: 'Stage',
  ASSIGNMENT: 'Assignment',
  SYSTEM: 'System',
};

const MANUAL_TYPES: ManualActivityType[] = ['CALL', 'EMAIL', 'MEETING', 'NOTE'];

function ActivityItem({ activity, canLog }: { activity: Activity; canLog: boolean }) {
  const del = useDeleteActivity();
  const toast = useToast();
  const manual = activity.metadata?.source === 'manual';
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-2)',
        padding: 'var(--space-3) 0',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <span className="ne-badge" style={{ alignSelf: 'flex-start' }}>
        {TYPE_LABEL[activity.type]}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        {activity.title ? <div style={{ fontWeight: 600 }}>{activity.title}</div> : null}
        {activity.description ? (
          <p style={{ margin: '2px 0 0', whiteSpace: 'pre-wrap' }}>{activity.description}</p>
        ) : null}
        <div
          style={{
            marginTop: 4,
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
          }}
        >
          {activity.actor?.displayName ?? 'System'} ·{' '}
          {new Date(activity.occurredAt).toLocaleString()}
        </div>
      </div>
      {canLog && manual ? (
        <button
          onClick={() =>
            del.mutate(activity.id, {
              onSuccess: () => toast.success('Activity deleted.'),
              onError: (err) =>
                toast.error(err instanceof Error ? err.message : 'Could not delete the activity.'),
            })
          }
          disabled={del.isPending}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--color-danger)',
            cursor: 'pointer',
          }}
        >
          Delete
        </button>
      ) : null}
    </div>
  );
}

export function ActivityTimeline({ title = 'Activity', query, attachTo }: Props) {
  const { hasPermission } = useAuth();
  const canLog = hasPermission('activities.log');
  const activities = useActivities({ pageSize: 100, sortOrder: 'desc', ...query });
  const create = useCreateActivity();
  const toast = useToast();

  const [type, setType] = useState<ManualActivityType>('CALL');
  const [summary, setSummary] = useState('');
  const [details, setDetails] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /** Mirrors the backend's `createActivityBody` limits (activities.schemas.ts),
   * plus a client-side "at least a summary or some detail" rule (not enforced
   * server-side, but a manual log entry with neither is not useful). */
  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};
    const trimmedSummary = summary.trim();
    const trimmedDetails = details.trim();
    if (trimmedSummary.length === 0 && trimmedDetails.length === 0) {
      errors.title = 'Add a short summary or some detail.';
    } else if (trimmedSummary.length > 200) {
      errors.title = 'Summary must be 200 characters or fewer.';
    }
    if (trimmedDetails.length > 5000) {
      errors.description = 'Details must be 5000 characters or fewer.';
    }
    return errors;
  }

  async function onLog(e: FormEvent) {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      return;
    }
    setError(null);
    try {
      await create.mutateAsync({
        type,
        title: summary.trim() || undefined,
        description: details.trim() || undefined,
        ...attachTo,
      });
      setSummary('');
      setDetails('');
      setFieldErrors({});
      toast.success('Activity logged.');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
          return;
        }
      }
      const message = err instanceof Error ? err.message : 'Could not log the activity.';
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Card title={title}>
      {canLog && attachTo ? (
        <form
          onSubmit={onLog}
          style={{ display: 'grid', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}
          noValidate
        >
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <select
              className="ne-input"
              style={{ flex: '0 0 120px' }}
              value={type}
              onChange={(e) => setType(e.target.value as ManualActivityType)}
            >
              {MANUAL_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABEL[t]}
                </option>
              ))}
            </select>
            <div style={{ flex: 1 }}>
              <TextField
                label="Summary"
                placeholder="Summary (e.g. Left a voicemail)"
                value={summary}
                onChange={(e) => {
                  setSummary(e.target.value);
                  setFieldErrors((f) => {
                    if (!f.title) return f;
                    const { title: _removed, ...rest } = f;
                    return rest;
                  });
                }}
                error={fieldErrors.title}
              />
            </div>
          </div>
          <div className="ne-field">
            <label className="ne-field__label" htmlFor="activity-details">
              Details (optional)
            </label>
            <textarea
              id="activity-details"
              className={['ne-input', fieldErrors.description ? 'ne-input--error' : '']
                .filter(Boolean)
                .join(' ')}
              rows={2}
              placeholder="Details (optional)"
              value={details}
              aria-invalid={fieldErrors.description ? true : undefined}
              onChange={(e) => {
                setDetails(e.target.value);
                setFieldErrors((f) => {
                  if (!f.description) return f;
                  const { description: _removed, ...rest } = f;
                  return rest;
                });
              }}
            />
            {fieldErrors.description ? (
              <span className="ne-field__error" role="alert">
                {fieldErrors.description}
              </span>
            ) : null}
          </div>
          <div>
            <Button type="submit" disabled={create.isPending}>
              Log activity
            </Button>
          </div>
        </form>
      ) : null}
      {error ? (
        <p style={{ margin: '0 0 var(--space-2)', color: 'var(--color-danger)' }}>{error}</p>
      ) : null}

      {activities.isPending ? (
        <PageState kind="loading" message="Loading activity…" />
      ) : activities.isError ? (
        <PageState
          kind="error"
          message={activities.error.message}
          onRetry={() => void activities.refetch()}
        />
      ) : activities.data.items.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>No activity yet.</p>
      ) : (
        <div>
          {activities.data.items.map((a) => (
            <ActivityItem key={a.id} activity={a} canLog={canLog} />
          ))}
        </div>
      )}
    </Card>
  );
}
